import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import {
  activePromoStatuses,
  availablePromoStatuses,
  FreeBetPromo,
  FreeSpinPromo,
  GigCommonPromo,
  GigPromo,
  MagicXPromo,
  ParsedPromo,
  PromoTypes,
  RewardPromo,
  UserGigQuery,
} from '../../components/Layout/Promotions/types';
import { useUserContext } from '../../providers/UserProvider';
import { UserProfileBonusStatus } from '../../types.__generated__';
import { APP_URLS, locale, xTenantId } from '../../consts';
import {
  UserBonusesQuery,
  useUserBonusesLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-promo-collection.generated';
import { Endpoint } from '../../models/api.model';
import { getImageUrl } from '../../utils/navigation';
import {
  useTagsObjectLazyQuery,
  TagsObjectQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import {
  UserFreeBetQuery,
  useUserFreeBetLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-bet-get.generated';
import {
  UserFreeSpinQuery,
  useUserFreeSpinLazyQuery,
} from '../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import { today } from '../../components/Layout/Promotions/consts';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { getActiveMxPromotions, getAvailablegMxPromotions } from '../../components/Layout/Promotions/services';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { activePromoType, loadPlatformPromotions } from '../../state/promotions';
import {
  MagicXDataGetAllQuery,
  useMagicXDataGetAllLazyQuery,
} from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-all-magicx-data.query.generated';
import { MagicxPromoGetAllQuery } from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/promos.query.generated';
import { setLoadPlatformPromotions } from '../../state/promotions/reducers';
import { ProductType } from '../../components/Layout/Promotions/PromotionsBlock/types';
import { PromoTagCategory } from './types';
import { getMagicXData } from '../../components/Layout/Promotions/utils';

export const useFetchMergedPromotions = (
  promoType: ProductType
): {
  mergedFilteredPromotions: ParsedPromo[];
  mergedHistoryPromotions: ParsedPromo[];
  isPromosLoading: boolean;
  promotionsLoadingError: unknown | ApolloError;
} => {
  const isUserAuthenticated = useAuth()?.isAuthenticated;
  const { userProfileId, user } = useUserContext();
  const isTargetPromotionsUrl = !useParams().id;
  const loadPlatformPromotionsState = useSelector(loadPlatformPromotions);
  const currentPromoType = useSelector(activePromoType);
  const dispatch = useDispatch();

  const isActivePromotions = promoType === ProductType.REWARDS;
  const resultPromoStatus: string[] = isActivePromotions ? activePromoStatuses : availablePromoStatuses;

  const [directusPromotions, setDirectusPromotions] = useState<ParsedPromo[] | null>(null);
  const [resultPromotions, setResultPromotions] = useState<ParsedPromo[]>([]);
  const [isPromosLoading, setIsPromotionsLoading] = useState<boolean>(true);
  const [fetchMagicXError, setFetchMagicXError] = useState<unknown>('');
  const [userTagBonuses, setUserTagBonuses] = useState<(string | null | undefined)[] | undefined>([]);
  const [magicXPromotions, setMagicXPromotions] = useState<(ParsedPromo | undefined)[]>([]);
  const [depositPromotions, setDepositPromotions] = useState<(ParsedPromo | undefined)[]>([]);
  const [rewardsPromotions, setRewardsPromotions] = useState<(ParsedPromo | undefined)[]>([]);

  const onQueryError = (e: ApolloError): void => console.error(e.message);

  const handleMagicXDirectusPromos = (data: MagicXDataGetAllQuery | MagicxPromoGetAllQuery): void =>
    setDirectusPromotions(
      ([...data.magicx_promotions] as typeof data.magicx_promotions & RewardPromo[])
        .filter((promo) => {
          const startDateTime = parseISO(`${promo.start_date}T${promo.start_time || '00:00:00'}`);

          if (!promo.end_date) {
            return isAfter(today, startDateTime);
          }

          const endDateTime = parseISO(`${promo.end_date}T${promo.end_time || '23:59:59'}`);

          return isAfter(today, startDateTime) && isBefore(today, endDateTime);
        })
        .map((promo) => {
          const mergedPromo = promo as typeof promo & RewardPromo;

          return {
            id: promo.id,
            startDate: `${promo.start_date}T${promo.start_time || '00:00:00'}`,
            endDate: `${promo.end_date}T${promo.end_time || '23:59:59'}`,
            bonusType: String(promo.__typename),
            products:
              mergedPromo.collection?.translations?.map((translation) =>
                translation?.display_name?.toLocaleLowerCase()
              ) || [],
            title: String(promo.translations?.[0]?.title),
            subtitle: String(promo.translations?.[0]?.subtitle || ''),
            description: String(promo.translations?.[0]?.description),
            ctaCopy: promo.translations?.[0]?.cta_copy,
            ctaUrl: String(promo.translations?.[0]?.cta_url),
            bonusId: mergedPromo.bonus_ID,
            tagId: mergedPromo.tag_ID,
            rewardId: mergedPromo.reward_ID,
            promoType: (mergedPromo.bonus_type || mergedPromo.reward_type)?.toLowerCase(),
            dmBonusId: mergedPromo.DM_Template_ID,
            dmMaxAmount: mergedPromo.DM_Max_Amount,
            imgPath: getImageUrl(promo.thumbnail?.filename_disk),
            wideImgPath: getImageUrl(promo.thumbnail_wide?.filename_disk),
            imgAlt: String(promo.reg_image_desktop?.title),
            pageUrl: `${APP_URLS.promotionsRoot}/${promo.translations?.[0]?.slug}`,
            successCTAGameInformation: {
              gameId: '',
            },
            ordering: mergedPromo.collection?.ordering,
            carouselType: mergedPromo.collection?.carousel_type,
            magicXSlug: mergedPromo.magicx_slug,
          };
        })
    );

  const handlePlatformPromos = useCallback(
    (data: UserGigQuery): void => {
      const gigBonuses = (data as UserBonusesQuery).userBonuses?.data;
      const gigFreeBets = (data as UserFreeBetQuery).userFreeBet?.data;
      const gigFreeSpins = (data as UserFreeSpinQuery).userFreeSpin?.data;

      const resGigBonuses = gigBonuses || gigFreeBets || gigFreeSpins || [];

      // First, handle the Gig bonuses merging as before
      const mergedPromotions: (ParsedPromo | undefined)[] =
        (resGigBonuses as GigCommonPromo[])
          .map((gigPromo: GigCommonPromo) => {
            const sdGigPromo = gigPromo as GigPromo;
            const freeSpin = gigPromo as FreeSpinPromo;
            const freeBet = gigPromo as FreeBetPromo;
            const magicXPromo = gigPromo as MagicXPromo;
            const sdGigPromoFreeBet = gigPromo as GigPromo | FreeBetPromo;

            const resPromoId =
              sdGigPromo?.bonusTemplateId || freeSpin?.freeSpinsTemplateId || freeBet?.freeBetTemplateId;

            // It's using as we can't currently rely on bonusTemplateId that's also placed for free bets and spins
            const cancellable = !(freeSpin?.freeSpinsTemplateId || freeBet?.freeBetTemplateId);

            const isFreeBet = !!freeBet?.freeBetTemplateId;

            const directusPromo = directusPromotions?.find(
              (dirPromo: ParsedPromo, index) =>
                (dirPromo.rewardId && dirPromo.rewardId === resPromoId) ||
                (dirPromo.dmBonusId && dirPromo.dmBonusId === sdGigPromo?.bonusTemplateId)
            );

            return directusPromo
              ? {
                  ...directusPromo,
                  id: sdGigPromo?.id || freeSpin?.id || freeBet?.id || magicXPromo?.id,
                  status: (gigPromo as GigPromo)?.status,
                  expiresOn: (gigPromo as GigPromo)?.expiresOn || magicXPromo.expiry,
                  createdOn: sdGigPromo?.createdOn,
                  wageringRequirementAmount: sdGigPromo?.wageringRequirementAmount,
                  wageredAmount: sdGigPromo?.wageredAmount,
                  balanceAmount: sdGigPromoFreeBet?.balanceAmount,
                  convertedAmount: sdGigPromoFreeBet?.convertedAmount,
                  cancellable,
                  isFreeBet,
                }
              : undefined;
          })
          .filter((dirPromo: ParsedPromo | undefined) => dirPromo) || [];

      gigBonuses
        ? setDepositPromotions(mergedPromotions)
        : setRewardsPromotions((prevPromotions) => [...prevPromotions, ...(mergedPromotions as ParsedPromo[])]);
    },
    [directusPromotions]
  );

  const handleMagicXPromos = (magicXPromotions: MagicXPromo[], { isActive }: { isActive: boolean }): void => {
    // First, handle the Gig bonuses merging as before
    const mergedMagicXPromotions: ParsedPromo[] =
      (magicXPromotions
        .map((magicXPromo) => {
          const directusPromo = directusPromotions?.find(
            (dirPromo: ParsedPromo) => dirPromo.magicXSlug === magicXPromo.slug
          );

          const {
            id: magicId,
            expiry: expiresOn,
            start: createdOn,
            milestones,
            achievementValue: wageredAmount,
            winAmount: balanceAmount,
            type,
          } = magicXPromo;
          const { value: wageringRequirementAmount, level: convertedAmount } = milestones?.[0] || {};
          const { magicXCta } = getMagicXData(magicXPromo);

          const status = isActive ? UserProfileBonusStatus.Claimed : UserProfileBonusStatus.Available;

          return directusPromo
            ? {
                ...directusPromo,
                magicId,
                status,
                expiresOn,
                createdOn,
                wageringRequirementAmount,
                wageredAmount,
                balanceAmount,
                convertedAmount,
                magicXCta,
                cancellable: false,
                type,
              }
            : undefined;
        })
        .filter((dirPromo: ParsedPromo | undefined) => dirPromo) as ParsedPromo[]) || [];

    setMagicXPromotions((prevPromotions) => [...prevPromotions, ...mergedMagicXPromotions]);
  };

  const addTaggedDepositPromos = (): void => {
    // Find any directus promos that match user bonus tags and mark them as available for opt in
    const taggedPromotions = directusPromotions
      ?.filter((dirPromo) => userTagBonuses?.includes(dirPromo.tagId))
      .map((promo) => ({ ...promo, status: UserProfileBonusStatus.Available }));

    setResultPromotions((prevResultPromos) => [...prevResultPromos, ...(taggedPromotions || [])]);
  };

  const handleUserTags = (userTagsData: TagsObjectQuery): void => {
    setUserTagBonuses(
      userTagsData.tagsObject
        ?.filter((item) => item.tag?.tagCategory?.name === PromoTagCategory.BONUS)
        .map((item) => item.tag?.id)
    );
  };

  const directusFetchData = {
    variables: {
      language: locale,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    notifyOnNetworkStatusChange: true,
    onCompleted: handleMagicXDirectusPromos,
    onError: onQueryError,
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  };

  const gigFetchData = {
    variables: {
      xTenantId,
      userProfileId,
      pageLimit: 1000,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handlePlatformPromos,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  };

  const [fetchDirectusMagicXPromotions, { error: fetchDirectusMagicXPromotionsError }] =
    useMagicXDataGetAllLazyQuery(directusFetchData);

  const [fetchBonusesGig, { error: fetchBonusesGigError }] = useUserBonusesLazyQuery(gigFetchData);
  const [fetchBonusesFreeBetGig, { error: fetchBonusesFreeBetGigError }] = useUserFreeBetLazyQuery(gigFetchData);
  const [fetchBonusesFreeSpinGig, { error: fetchBonusesFreeSpinGigError }] = useUserFreeSpinLazyQuery(gigFetchData);

  const [fetchUserTags, { error: fetchUserTagsError }] = useTagsObjectLazyQuery({
    variables: {
      xTenantId,
      externalResource: 'UserProfileId',
      externalResourceId: userProfileId,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    onCompleted: handleUserTags,
    notifyOnNetworkStatusChange: true,
    onError: onQueryError,
    context: { endpoint: Endpoint.appAPI },
  });

  const promotionsLoadingError =
    fetchDirectusMagicXPromotionsError ||
    fetchBonusesGigError ||
    fetchBonusesFreeBetGigError ||
    fetchBonusesFreeSpinGigError ||
    fetchUserTagsError ||
    fetchMagicXError;

  const fetchMagicXPromotions = async (): Promise<void> => {
    try {
      const activeMXPromotions = await getActiveMxPromotions(user?.email);
      const receivedAvailableMXPromotions = await getAvailablegMxPromotions(user?.email);

      // Filter promotions that may be available and active at the same time
      const availableMXPromotions = receivedAvailableMXPromotions.filter(
        (availablePromo) => !activeMXPromotions.find((activePromo) => activePromo.slug === availablePromo.slug)
      );

      handleMagicXPromos(activeMXPromotions, { isActive: true });
      handleMagicXPromos(availableMXPromotions, { isActive: false });
    } catch (error) {
      setFetchMagicXError(error);
    }
  };

  const getHistoryPromotions = (): ParsedPromo[] | null | undefined => {
    const nonHistoryStatuses: string[] = [
      UserProfileBonusStatus.InProgress,
      UserProfileBonusStatus.Claimed,
      UserProfileBonusStatus.Available,
    ];

    return resultPromotions?.filter((promo) => {
      const promoStatus = String(promo.status);

      return !nonHistoryStatuses.includes(promoStatus);
    });
  };

  const findTargetIndex = (handledPromotions: ParsedPromo[], searchPromo: ParsedPromo): number =>
    handledPromotions.findIndex((promoIndexed) => {
      const searchGigId = searchPromo.bonusId;
      const searchMagicXId = searchPromo.magicXSlug;

      const gigIdMatch = searchGigId && searchGigId === promoIndexed.bonusId;
      const magicXIdMatch = searchMagicXId && searchMagicXId === promoIndexed.magicXSlug;

      return gigIdMatch || magicXIdMatch;
    });

  const historyPromotions = useMemo(
    () => getHistoryPromotions(),
    [resultPromotions, directusPromotions, isUserAuthenticated, isActivePromotions, userTagBonuses]
  );

  const filteredPromotions = useMemo(() => {
    const indexStore: Record<string, number> = {};
    const composedPromotions: ParsedPromo[] = isUserAuthenticated
      ? [
          ...(rewardsPromotions as ParsedPromo[]),
          ...(depositPromotions as ParsedPromo[]),
          ...(magicXPromotions as ParsedPromo[]),
        ]
      : resultPromotions;

    const handledPromotions = composedPromotions?.filter((promo) =>
      isUserAuthenticated ? resultPromoStatus.includes(String(promo.status)) : promo
    );

    // Mark repeated promotions with different revision
    composedPromotions.length &&
      handledPromotions.forEach((promo, index) => {
        const promoId = String(promo.bonusId);
        const actIndex = (indexStore[promoId] || 0) + 1;
        const targetIndex = findTargetIndex(handledPromotions, promo);

        if (!(targetIndex === -1 || targetIndex === index)) {
          handledPromotions[index].pageSubUrl = String(actIndex);
          indexStore[promoId] = actIndex;
        }
      });

    const seenPromos = new Set<string>();
    const uniquePromos = handledPromotions.filter((promo) => {
      const key = promo.id || '';

      if (seenPromos.has(key)) return false;
      seenPromos.add(key);

      return true;
    });

    return uniquePromos;
  }, [
    resultPromoStatus,
    resultPromotions,
    directusPromotions,
    rewardsPromotions,
    depositPromotions,
    magicXPromotions,
    isUserAuthenticated,
  ]);

  useEffect(() => {
    (async (): Promise<void> => {
      if (isTargetPromotionsUrl && !directusPromotions?.length) {
        await fetchDirectusMagicXPromotions();
        !isUserAuthenticated && setIsPromotionsLoading(false);
      }
    })();

    dispatch(setLoadPlatformPromotions(true));
  }, [isTargetPromotionsUrl]);

  useEffect(() => {
    directusPromotions?.length &&
      (async (): Promise<void> => {
        if (isTargetPromotionsUrl && loadPlatformPromotionsState) {
          if (isUserAuthenticated && user?.email) {
            setIsPromotionsLoading(true);
            const shouldCallInit = !currentPromoType;
            const shouldCallWagerAndGet = shouldCallInit || currentPromoType === PromoTypes.WAGER_AND_GET;
            const shouldCallDeposit = shouldCallInit || currentPromoType === PromoTypes.DEPOSIT_MATCH;

            if (shouldCallInit) {
              await fetchBonusesFreeBetGig();
              await fetchBonusesFreeSpinGig();
            }

            if (shouldCallWagerAndGet) {
              setMagicXPromotions([]);
              await fetchMagicXPromotions();
            }

            if (shouldCallDeposit) {
              setDepositPromotions([]);
              await fetchUserTags();
              await fetchBonusesGig();
            }

            dispatch(setLoadPlatformPromotions(false));
            setIsPromotionsLoading(false);
          } else {
            setResultPromotions(directusPromotions);
          }
        }
      })();
  }, [directusPromotions, user?.email, loadPlatformPromotionsState]);

  useEffect(() => {
    userTagBonuses?.length && addTaggedDepositPromos();
  }, [userTagBonuses]);

  return {
    mergedFilteredPromotions: filteredPromotions || [],
    mergedHistoryPromotions: historyPromotions || [],
    isPromosLoading,
    promotionsLoadingError,
  };
};
